import type {
  PlayerData,
  PlayerRequestData,
  PlayersQueryParams
} from '@library/types/player';
import {
  useCreatePlayerMutation,
  useGetPlayersQuery,
  useUpdatePlayerMutation
} from '@bo-services/playersApi';
import { useCallback } from 'react';

export const usePlayers = ({
  playersQueryParams,
  currentItemId,
  setSubmitMessage
}: {
  playersQueryParams: PlayersQueryParams;
  currentItemId: string;
  setSubmitMessage: (message: string) => void;
}) => {
  const {
    data: response,
    isError,
    isLoading,
    isSuccess
  } = useGetPlayersQuery(playersQueryParams);

  const [createPlayer] = useCreatePlayerMutation();
  const [updatePlayer] = useUpdatePlayerMutation();

  const playersData = response?.data.map((player) => {
    return {
      combo: player.attributes.combo || '',
      createdAt: player.attributes.created_at,
      displayName: player.attributes.display_name,
      fantasyDataId: player.attributes.fantasy_data_id || '',
      id: player.id,
      imageUrl: player.attributes.image_url,
      leagueId: player.relationships?.league?.data.id,
      leagueName: response.included.find(
        (leagueInfo) =>
          leagueInfo.type === 'league' &&
          leagueInfo.id === player.relationships?.league?.data.id
      )?.attributes.name,
      market: player.attributes.market,
      name: player.attributes.name,
      oddsjamId: player.attributes.oddsjam_id,
      oldLeague: player.attributes.old_league,
      position: player.attributes.position,
      sportsradarId: player.attributes.sportsradar_id,
      statAverages: player.relationships?.stat_averages?.data
        .map((d) => d.id)
        .join(', '),
      swishId: player.attributes.swish_id || '',
      team: player.attributes.team,
      teamData: player.relationships?.team_data?.data.id,
      teamName: player.attributes.team_name,
      updatedAt: player.attributes.updated_at
    };
  }) as Array<PlayerData>;

  const mapPlayerDataToRequest = useCallback(
    (playerFormData: Partial<PlayerData>): PlayerRequestData => {
      const {
        combo,
        displayName,
        fantasyDataId,
        imageUrl,
        leagueId: playerLeagueId,
        market,
        name,
        oddsjamId,
        position,
        sportsradarId,
        swishId,
        team,
        teamName
      } = playerFormData;
      return {
        combo: combo as boolean,
        display_name: displayName,
        fantasy_data_id: Number(fantasyDataId),
        image_url: imageUrl || '',
        league_id: Number(playerLeagueId),
        market,
        name: name || '',
        oddsjam_id: oddsjamId,
        position: position || '',
        sportsradar_id: sportsradarId || '',
        swish_id: Number(swishId),
        team: team || '',
        team_name: teamName
      };
    },
    []
  );

  const handleUpdatePlayer = useCallback(
    async (formData: Partial<PlayerData>) => {
      await updatePlayer({
        id: currentItemId,
        data: mapPlayerDataToRequest(formData)
      })
        .unwrap()
        .then((payload) => {
          // eslint-disable-next-line no-console
          console.log('fulfilled', payload);
          setSubmitMessage('Player updated');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('rejected', error);
          setSubmitMessage(`Couldn't update Player - ${error}`);
        });
    },
    [updatePlayer, currentItemId, mapPlayerDataToRequest, setSubmitMessage]
  );

  const handleCreatePlayer = useCallback(
    async (formData: Partial<PlayerData>) => {
      await createPlayer(mapPlayerDataToRequest(formData))
        .unwrap()
        .then((payload) => {
          // eslint-disable-next-line no-console
          console.log('fulfilled', payload);
          setSubmitMessage('Player created');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('rejected', error);
          setSubmitMessage(`Couldn't create Player - ${error}`);
        });
    },
    [createPlayer, mapPlayerDataToRequest, setSubmitMessage]
  );

  return {
    createPlayer: handleCreatePlayer,
    isError,
    isLoading,
    isSuccess,
    mapPlayerDataToRequest,
    meta: response?.meta || {},
    page: response?.meta.current_page,
    players: playersData || [],
    totalPages: response?.meta.total_pages || 0,
    updatePlayer: handleUpdatePlayer
  };
};
